import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BookOpen, Activity, Briefcase, Globe, Users, ArrowRight, CheckCircle2 } from 'lucide-react';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6
  }
};
export function Programs() {
  const programs = [{
    id: 'education',
    title: 'Education & Academics',
    tagline: 'Empower Through Learning',
    icon: BookOpen,
    desc: "Our education programs blend traditional African knowledge with modern skills development. From language and cultural arts to coding and robotics, we're preparing the next generation of leaders.",
    includes: ['Academic Enrichment', 'Cultural Arts Training', 'Innovation & Tech', 'After-School Programs', 'Scholarships & Mentorship'],
    audience: 'Children, Teens, Young Adults',
    img: 'https://images.unsplash.com/photo-1577896851231-70ef18881754?q=80&w=2940&auto=format&fit=crop'
  }, {
    id: 'health',
    title: 'Health & Wellness',
    tagline: 'Healthy Communities, Thriving Futures',
    icon: Activity,
    desc: 'We promote health awareness and behavioral wellness for children, youth, and adults. Our programs focus on preventing disease, building healthy habits, and creating awareness around critical health issues.',
    includes: ['Health Awareness Seminars', 'Behavioral Health Education', 'Youth Wellness Programs', 'Adult Health Outreach'],
    audience: 'All ages',
    img: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=2940&auto=format&fit=crop'
  }, {
    id: 'business',
    title: 'Business & Entrepreneurship',
    tagline: 'Build, Create, Succeed',
    icon: Briefcase,
    desc: "Transform ideas into impact with our business and entrepreneurship training. Whether you're just starting or scaling up, our programs provide the skills, mentorship, and connections you need to succeed.",
    includes: ['Entrepreneurship Seminars', 'Business Skills Training', 'Mentorship Programs', 'Innovation Labs'],
    audience: 'Entrepreneurs, Young Professionals',
    img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2940&auto=format&fit=crop'
  }, {
    id: 'culture',
    title: 'Cultural Heritage & Travel',
    tagline: 'Discover Africa. Celebrate Culture.',
    icon: Globe,
    desc: 'Experience authentic African culture through immersive tours, exhibitions, and cultural celebrations. From local heritage tours to international cultural showcases, we bring Africa to life.',
    includes: ['Cultural Heritage Tours', 'Museum Visits & Exhibitions', 'Annual & Monthly Events', 'International Showcases'],
    audience: 'Culture Enthusiasts, Families, Travelers',
    img: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?q=80&w=2872&auto=format&fit=crop'
  }, {
    id: 'outreach',
    title: 'Community Outreach & Impact',
    tagline: 'Giving Back. Building Together.',
    icon: Users,
    desc: 'Support African communities through direct outreach, scholarships, and donations. Your contribution directly impacts lives and transforms communities.',
    includes: ['Donate to Scholarships', 'Support Community Programs', 'Sponsor Youth Training', 'Volunteer Your Time'],
    audience: 'Donors, Volunteers, Community Members',
    img: 'https://images.unsplash.com/photo-1531206715517-5c0ba140b2b8?q=80&w=2940&auto=format&fit=crop'
  }];
  return <div className="pt-0 bg-warmwhite" data-id="element-1167" data-mp="fivyturmt78g">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-navy overflow-hidden" data-id="element-1168" data-mp="aorwjotb9ndp">
        <div className="absolute inset-0 z-0" data-id="element-1169" data-mp="3rco102jtmcj0">
          <img src="https://images.unsplash.com/photo-1542810634-71277d95dc8c?q=80&w=2940&auto=format&fit=crop" alt="Education and Programs" className="w-full h-full object-cover opacity-20" data-id="element-1170" data-mp="3rg24xf84utsk" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-transparent" data-id="element-1171" data-mp="3mdun490mgjib" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-4xl" data-id="element-1172" data-mp="dzugdic7a8dh">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-1173" data-mp="iwukhnphp11y">
            Transform Lives Through Learning
          </h1>
          <p className="text-xl text-white/80 leading-relaxed" data-id="element-1174" data-mp="54ep992ty7bl">
            Explore our comprehensive programs designed to empower, educate, and
            inspire communities across Africa and the world.
          </p>
        </div>
      </section>

      {/* Programs List */}
      <section className="py-12 lg:py-16" data-id="element-1175" data-mp="11pyfpdicvlyo">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1176" data-mp="2mej1wafqynzy">
          <div className="space-y-16 lg:space-y-24" data-id="element-1177" data-mp="2mczkuhm9o8x4">
            {programs.map((prog, index) => <motion.div key={prog.id} variants={fadeIn} initial="initial" whileInView="whileInView" className={`flex flex-col ${index % 2 !== 0 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 lg:gap-20 items-center`} data-id="element-1178" data-mp="2hffw2nry0dqx" data-mpi={index}>
                <div className="lg:w-1/2 w-full order-1 lg:order-none" data-id="element-1179" data-mp="23i14pdi4pvwb">
                  <div className="relative rounded-3xl overflow-hidden shadow-premium group" data-id="element-1180" data-mp="1yienksz6jvi8">
                    <img src={prog.img} alt={prog.title} className="w-full h-[300px] md:h-[400px] object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-1181" data-mp="2dew21t2o1q90" />
                    <div className="absolute top-4 left-4 lg:top-6 lg:left-6 bg-white/90 backdrop-blur-md p-3 lg:p-4 rounded-2xl shadow-lg" data-id="element-1182" data-mp="3jugedb1p0ori">
                      <prog.icon className="w-6 h-6 lg:w-8 lg:h-8 text-gold" data-id="element-1183" data-mp="3qwldx95lhe7l" />
                    </div>
                  </div>
                </div>

                <div className="lg:w-1/2 w-full order-2 lg:order-none" data-id="element-1184" data-mp="4q6i1fl0qaxe">
                  <h2 className="text-3xl lg:text-4xl font-serif font-bold text-navy mb-2" data-id="element-1185" data-mp="4o3k89dmjdi4">
                    {prog.title}
                  </h2>
                  <p className="text-gold font-semibold tracking-wide uppercase text-xs lg:text-sm mb-4 lg:mb-6" data-id="element-1186" data-mp="387ofct35zzjk">
                    {prog.tagline}
                  </p>
                  <p className="text-mediumgray text-base lg:text-lg mb-6 lg:mb-8 leading-relaxed" data-id="element-1187" data-mp="3i52vssp38obv">
                    {prog.desc}
                  </p>

                  <div className="bg-white p-5 lg:p-6 rounded-2xl border border-softgray shadow-sm mb-6 lg:mb-8" data-id="element-1188" data-mp="3mwkutb7jweu7">
                    <h4 className="font-bold text-navy mb-3 lg:mb-4" data-id="element-1189" data-mp="2pg5f2u0iyzl8">
                      Programs Include:
                    </h4>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 lg:gap-3" data-id="element-1190" data-mp="1bk42eklqv81b">
                      {prog.includes.map((item, i) => <li key={i} className="flex items-start gap-2 text-mediumgray text-sm" data-id="element-1191" data-mp="17k3mswmwsqso" data-mpi={i}>
                          <CheckCircle2 className="w-4 h-4 lg:w-5 lg:h-5 text-gold shrink-0" data-id="element-1192" data-mp="xle4x3v0j6cf" />
                          {item}
                        </li>)}
                    </ul>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1193" data-mp="nu01ikzxxme0">
                    <Link to="/contact" className="w-full sm:w-auto px-6 lg:px-8 py-3 lg:py-4 bg-navy hover:bg-navy-light text-white rounded-full font-semibold transition-all text-center shadow-md hover:-translate-y-1" data-id="element-1194" data-mp="ju9duq5pnmdp">
                      Apply for Program
                    </Link>
                    <span className="text-xs lg:text-sm text-mediumgray font-medium bg-warmwhite px-4 py-2 rounded-full w-full sm:w-auto text-center" data-id="element-1195" data-mp="4y9weowpo9vw">
                      Target: {prog.audience}
                    </span>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 lg:py-16 bg-white border-t border-softgray text-center" data-id="element-1196" data-mp="1op70p6lt2amh">
        <div className="container mx-auto px-6" data-id="element-1197" data-mp="1a0knbafqnsxw">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-8" data-id="element-1198" data-mp="1eslvol9sczin">
            Ready to get involved?
          </h2>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1199" data-mp="112qre0lvz1b9">
            <Link to="/contact" className="px-8 py-4 bg-navy text-white hover:bg-navy-light rounded-full font-bold transition-all shadow-lg hover:-translate-y-1" data-id="element-1200" data-mp="160aelq0kaofc">
              Apply for Program
            </Link>
            <Link to="/donate" className="px-8 py-4 bg-gold text-white hover:bg-gold-light rounded-full font-bold transition-all shadow-lg hover:-translate-y-1" data-id="element-1201" data-mp="w1kwbw4r71yk">
              Donate
            </Link>
            <Link to="/volunteer" className="px-8 py-4 bg-transparent border-2 border-navy text-navy hover:bg-navy hover:text-white rounded-full font-bold transition-all hover:-translate-y-1" data-id="element-1202" data-mp="meh18k8fxpca">
              Volunteer
            </Link>
          </div>
        </div>
      </section>
    </div>;
}